import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  DEFAULT_PRINTERS_SETTINGS,
  DEFAULT_PRINTER_CONFIG,
  IPrinterConfig,
  IPrintersSettings,
  PrinterConnectionType,
  PrinterProtocol,
  PrinterPurpose,
} from '../models';
import { IntegrationsService } from './integrations.service';
import { PrinterConnectionService } from './printer-connection.service';

const PRINTERS_INTEGRATION_ID = 'printers';

/**
 * Service for managing printer configurations
 * Handles CRUD operations, default assignments, and persists to backend via IntegrationsService
 */
@Injectable({
  providedIn: 'root',
})
export class PrinterManagementService {
  private readonly integrationsService = inject(IntegrationsService);
  private readonly connectionService = inject(PrinterConnectionService);
  private readonly translateService = inject(TranslateService);

  // Internal state
  private readonly printersSettingsSource = signal<IPrintersSettings>(DEFAULT_PRINTERS_SETTINGS);

  // Public readonly signals
  readonly printers = computed(() => this.printersSettingsSource().printers);
  readonly defaultPrinters = computed(() => this.printersSettingsSource().defaultPrinters);
  readonly globalSettings = computed(() => ({
    enableTicketsPrinting: this.printersSettingsSource().enableTicketsPrinting,
    printOnOrderConfirm: this.printersSettingsSource().printOnOrderConfirm,
    autoCut: this.printersSettingsSource().autoCut,
    openCashDrawer: this.printersSettingsSource().openCashDrawer,
    compactMode: this.printersSettingsSource().compactMode,
    splitTicketsByProductType: this.printersSettingsSource().splitTicketsByProductType,
    printLocale: this.printersSettingsSource().printLocale,
  }));

  // Computed: Get printers by purpose
  readonly receiptPrinters = computed(() => this.printers().filter((p) => p.purposes.includes(PrinterPurpose.RECEIPT)));
  readonly kitchenPrinters = computed(() => this.printers().filter((p) => p.purposes.includes(PrinterPurpose.KITCHEN)));
  readonly barPrinters = computed(() => this.printers().filter((p) => p.purposes.includes(PrinterPurpose.BAR)));
  readonly reportsPrinters = computed(() => this.printers().filter((p) => p.purposes.includes(PrinterPurpose.REPORTS)));

  // Integration state
  readonly isEnabled = computed(() => {
    const integration = this.integrationsService.integrations().find((i) => i.integrationId === PRINTERS_INTEGRATION_ID);
    return integration?.isEnabled ?? false;
  });

  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  constructor() {
    // Sync settings from integrations service when it loads
    effect(() => {
      const integrations = this.integrationsService.integrations();
      const printersIntegration = integrations.find((i) => i.integrationId === PRINTERS_INTEGRATION_ID);

      if (printersIntegration?.settings) {
        const settings = printersIntegration.settings as unknown as IPrintersSettings;
        if (settings.printers) {
          this.printersSettingsSource.set(settings);
        }
      }
    });
  }

  /**
   * Add a new printer configuration
   */
  async addPrinter(printerInput: Partial<IPrinterConfig>): Promise<IPrinterConfig> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      if (!printerInput.name) {
        throw new Error(this.translateService.instant('printers.errors.name_required'));
      }

      const newPrinter: IPrinterConfig = {
        ...DEFAULT_PRINTER_CONFIG,
        ...printerInput,
        id: printerInput.id || this.generatePrinterId(),
        name: printerInput.name,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Validate required fields
      this.validatePrinterConfig(newPrinter);

      // Update local state
      this.printersSettingsSource.update((settings) => ({
        ...settings,
        printers: [...settings.printers, newPrinter],
      }));

      // Persist to backend
      await this.persistSettings();

      return newPrinter;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : this.translateService.instant('printers.errors.add_failed');
      this.error.set(errorMessage);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Update an existing printer configuration
   */
  async updatePrinter(printerId: string, updates: Partial<IPrinterConfig>): Promise<IPrinterConfig> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const currentPrinters = this.printers();
      const printerIndex = currentPrinters.findIndex((p) => p.id === printerId);

      if (printerIndex === -1) {
        throw new Error(this.translateService.instant('printers.errors.printer_not_found', { id: printerId }));
      }

      const updatedPrinter: IPrinterConfig = {
        ...currentPrinters[printerIndex],
        ...updates,
        id: printerId, // Ensure ID doesn't change
        updatedAt: new Date(),
      };

      // Validate
      this.validatePrinterConfig(updatedPrinter);

      // Update local state
      this.printersSettingsSource.update((settings) => ({
        ...settings,
        printers: settings.printers.map((p) => (p.id === printerId ? updatedPrinter : p)),
      }));

      // Persist to backend
      await this.persistSettings();

      return updatedPrinter;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : this.translateService.instant('printers.errors.update_failed');
      this.error.set(errorMessage);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Remove a printer configuration
   */
  async removePrinter(printerId: string): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      // Disconnect if connected
      await this.connectionService.disconnect(printerId);

      // Update local state
      this.printersSettingsSource.update((settings) => {
        // Remove from printers array
        const newPrinters = settings.printers.filter((p) => p.id !== printerId);

        // Remove from defaults if it was a default printer
        const newDefaults = { ...settings.defaultPrinters };
        for (const purpose of Object.keys(newDefaults) as PrinterPurpose[]) {
          if (newDefaults[purpose] === printerId) {
            newDefaults[purpose] = undefined;
          }
        }

        return {
          ...settings,
          printers: newPrinters,
          defaultPrinters: newDefaults,
        };
      });

      // Persist to backend
      await this.persistSettings();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : this.translateService.instant('printers.errors.remove_failed');
      this.error.set(errorMessage);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Get a printer by ID
   */
  getPrinter(printerId: string): IPrinterConfig | undefined {
    return this.printers().find((p) => p.id === printerId);
  }

  /**
   * Get the default printer for a specific purpose
   */
  getDefaultPrinter(purpose: PrinterPurpose): IPrinterConfig | undefined {
    const defaults = this.defaultPrinters();
    const defaultId = defaults[purpose];

    if (defaultId) {
      return this.getPrinter(defaultId);
    }

    // Fallback: return first printer that supports this purpose
    const printersForPurpose = this.printers().filter((p) => p.purposes.includes(purpose));
    return printersForPurpose[0];
  }

  /**
   * Set the default printer for a specific purpose or product type
   */
  async setDefaultPrinter(purpose: PrinterPurpose | string, printerId: string | undefined): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      // Validate printer exists and supports the purpose (if it's a standard purpose)
      if (printerId) {
        const printer = this.getPrinter(printerId);
        if (!printer) {
          throw new Error(this.translateService.instant('printers.errors.printer_not_found', { id: printerId }));
        }

        // Automatically add the purpose if it's missing and it is a standard purpose
        if (Object.values(PrinterPurpose).includes(purpose as PrinterPurpose) && !printer.purposes.includes(purpose as PrinterPurpose)) {
          const updatedPurposes = [...printer.purposes, purpose as PrinterPurpose];
          await this.updatePrinter(printerId, { purposes: updatedPurposes });
        }
      }

      // Update local state
      this.printersSettingsSource.update((settings) => ({
        ...settings,
        defaultPrinters: {
          ...settings.defaultPrinters,
          [purpose]: printerId,
        },
      }));

      // Persist to backend
      await this.persistSettings();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : this.translateService.instant('printers.errors.set_default_failed');
      this.error.set(errorMessage);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Update global printing settings
   */
  async updateGlobalSettings(
    settings: Partial<
      Pick<IPrintersSettings, 'enableTicketsPrinting' | 'printOnOrderConfirm' | 'autoCut' | 'openCashDrawer' | 'compactMode'>
    >,
  ): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      // Update local state
      this.printersSettingsSource.update((currentSettings) => ({
        ...currentSettings,
        ...settings,
      }));

      // Persist to backend
      await this.persistSettings();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : this.translateService.instant('printers.errors.update_settings_failed');
      this.error.set(errorMessage);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Add a USB printer after user selection
   */
  async addUSBPrinter(device: USBDevice, name: string, purposes: PrinterPurpose[]): Promise<IPrinterConfig> {
    const printerConfig: Partial<IPrinterConfig> = {
      name,
      connectionType: PrinterConnectionType.WEB_USB,
      protocol: PrinterProtocol.ESC_POS,
      purposes,
      usbVendorId: device.vendorId,
      usbProductId: device.productId,
      usbSerialNumber: device.serialNumber || undefined,
      isEnabled: true,
    };

    return this.addPrinter(printerConfig);
  }

  /**
   * Add a manual IP printer
   */
  async addIPPrinter(name: string, ipAddress: string, port: number, purposes: PrinterPurpose[]): Promise<IPrinterConfig> {
    const printerConfig: Partial<IPrinterConfig> = {
      name,
      connectionType: PrinterConnectionType.MANUAL_IP,
      protocol: PrinterProtocol.ESC_POS,
      purposes,
      ipAddress,
      port,
      isEnabled: true,
    };

    return this.addPrinter(printerConfig);
  }

  /**
   * Test connection to a printer
   */
  async testConnection(printerId: string): Promise<boolean> {
    const printer = this.getPrinter(printerId);
    if (!printer) {
      throw new Error(this.translateService.instant('printers.errors.printer_not_found', { id: printerId }));
    }

    return this.connectionService.connect(printer);
  }

  /**
   * Reload settings from backend
   */
  refresh(): void {
    this.integrationsService.refreshIntegrations();
  }

  // ============================================
  // Private Methods
  // ============================================

  /**
   * Persist current settings to backend via IntegrationsService
   */
  private async persistSettings(): Promise<void> {
    const settings = this.printersSettingsSource();

    try {
      await this.integrationsService.updateIntegrationSettings(PRINTERS_INTEGRATION_ID, settings as unknown as Record<string, unknown>);
    } catch (err) {
      // If integration doesn't exist yet, it will be created when enabled
      console.warn(this.translateService.instant('printers.errors.persist_failed'), err);
      throw err;
    }
  }

  /**
   * Generate a unique printer ID
   */
  private generatePrinterId(): string {
    return `printer_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Validate printer configuration
   */
  private validatePrinterConfig(config: IPrinterConfig): void {
    if (!config.name || config.name.trim().length === 0) {
      throw new Error(this.translateService.instant('printers.errors.name_required'));
    }

    if (!config.connectionType) {
      throw new Error(this.translateService.instant('printers.errors.connection_type_required'));
    }

    if (config.connectionType === PrinterConnectionType.MANUAL_IP) {
      if (!config.ipAddress) {
        throw new Error(this.translateService.instant('printers.errors.ip_address_required'));
      }
      // Basic IP validation
      const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
      if (!ipRegex.test(config.ipAddress)) {
        throw new Error(this.translateService.instant('printers.errors.invalid_ip_format'));
      }
    }

    if (config.connectionType === PrinterConnectionType.WEB_USB) {
      if (!config.usbVendorId || !config.usbProductId) {
        throw new Error(this.translateService.instant('printers.errors.usb_id_required'));
      }
    }
    // Purposes are configured in the Printing Rules tab, not during printer creation
  }
}
